// Commission corrections: PURE logic (no supabase import, no I/O).
//
// When an invoice is adjusted or voided AFTER its commission was marked Paid,
// the paid record in rep_commission_payments is never rewritten. A signed
// correction line is recorded in rep_commission_corrections (migration 0031)
// instead. Every correction is measured against what was PAID (frozen amount
// and rate of the paid row), not against the previous invoice total:
//
//   accounted(I) = paid(I) + sum(delta of corrections anchored to that payout)
//   target(I)    = 0 for a void, else newTotal x frozen paid rate / 100
//   delta        = target - accounted           (no row when |delta| < 0.005)
//
// A correction is "anchored" to (invoice_id, paid_batch_id): it only counts
// while a paid row with that pair exists, so an Undo of the payout makes open
// corrections inert and a re-pay (new batch id) can never revive them.

export const TOLERANCE = 0.005;

export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const num = (v) => {
  if (v === null || v === undefined || v === "") return NaN;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};
const str = (v) => (v === null || v === undefined ? "" : String(v));
const sum = (list, pick) => (list || []).reduce((s, x) => {
  const n = Number(pick(x));
  return s + (Number.isFinite(n) ? n : 0);
}, 0);

export const STATUSES_COUNTED = ["open", "settled", "waived"];

// What was paid for one paid row: commission_amount, else the legacy `amount`.
// NaN when neither is usable.
export function paidRowCommission(row) {
  if (!row) return NaN;
  const ca = num(row.commission_amount);
  if (Number.isFinite(ca)) return ca;
  return num(row.amount);
}

// Frozen rate (percent) of a paid row: its own commission_rate when finite and
// > 0, else commission_amount / invoice_amount x 100 when invoice_amount > 0
// (0012 defaults commission_rate to 0). null when neither yields a usable rate.
// A later change of the rep's CURRENT rate never enters here.
export function paidRowRate(row) {
  if (!row) return null;
  const r = num(row.commission_rate);
  if (Number.isFinite(r) && r > 0) return r;
  const inv = num(row.invoice_amount);
  const ca = paidRowCommission(row);
  if (Number.isFinite(inv) && inv > 0 && Number.isFinite(ca)) {
    const derived = (ca / inv) * 100;
    if (Number.isFinite(derived) && derived > 0) return derived;
  }
  return null;
}

export function buildCorrectionClientId(invoiceId, batchId, ordinal) {
  return `${str(invoiceId)}|${str(batchId)}|n${ordinal}`;
}

// Corrections anchored to one payout of one invoice (every status counts as
// "accounted": a waived one is not recreated, a settled one is money out).
export function anchoredCorrections(corrections, invoiceId, batchId) {
  return (corrections || []).filter((c) => c
    && str(c.invoice_id) === str(invoiceId)
    && str(c.paid_batch_id) === str(batchId)
    && STATUSES_COUNTED.includes(c.status));
}

// ── computeCorrection ────────────────────────────────────────────────────────
// paidRows    : rep_commission_payments rows for ONE invoice (fresh read).
// corrections : rep_commission_corrections rows already recorded for it.
// newTotal    : the invoice's new total (ignored for a void).
// isVoid      : the invoice is being / has been cancelled.
// legacyPaid  : optional; the invoice only has a per-device legacy payout that
//               is not in Supabase (unknown amount): refused.
// Returns { ok:true, delta, ordinal, client_id, ...column snapshot }
//       | { ok:true, none:true, code, reason }     nothing to record
//       | { ok:false, code, reason }               refused, needs a human
export function computeCorrection({ paidRows, corrections, newTotal, isVoid, legacyPaid } = {}) {
  const rows = Array.isArray(paidRows) ? paidRows.filter(Boolean) : [];
  if (rows.length === 0) {
    if (legacyPaid) {
      return { ok: false, code: "legacy_only", reason: "Legacy payout not migrated yet: open the Commission tab on the browser that holds it so the backfill runs, then try again." };
    }
    // Never paid: the invoice simply re-prices or drops out of Pending.
    return { ok: true, none: true, code: "not_paid", reason: "Commission for this invoice has not been paid: nothing to correct." };
  }
  if (rows.length > 1) {
    return { ok: false, code: "duplicate_paid_rows", reason: "Duplicate payout rows exist for this invoice: review manually before any correction." };
  }
  const row = rows[0];
  const repId = str(row.rep_id).trim();
  if (!repId) {
    return { ok: false, code: "empty_rep_id", reason: "The payout row has no rep id, so the rep could not see a correction: review manually." };
  }
  const batchId = str(row.batch_id).trim();
  if (!batchId) {
    return { ok: false, code: "no_batch_id", reason: "The payout row has no batch id: review manually." };
  }
  const paid = paidRowCommission(row);
  if (!Number.isFinite(paid)) {
    return { ok: false, code: "unusable_paid_amount", reason: "The payout row has no usable paid amount: review manually." };
  }
  const rate = paidRowRate(row);
  if (rate === null) {
    return { ok: false, code: "unusable_rate", reason: "The payout row has no usable commission rate: review manually." };
  }
  const total = Number(newTotal);
  if (!isVoid && (!Number.isFinite(total) || total < 0)) {
    return { ok: false, code: "bad_total", reason: "The new invoice total is not a valid amount." };
  }

  const invoiceId = str(row.invoice_id);
  const anchored = anchoredCorrections(corrections, invoiceId, batchId);
  const accounted = paid + sum(anchored, (c) => c.delta);
  const target = isVoid ? 0 : (total * rate) / 100;
  const delta = target - accounted;
  if (Math.abs(delta) < TOLERANCE || round2(delta) === 0) {
    return { ok: true, none: true, code: "already_accounted", reason: "The paid commission already matches this invoice." };
  }
  const ordinal = anchored.length + 1;
  return {
    ok: true,
    delta: round2(delta),
    ordinal,
    client_id: buildCorrectionClientId(invoiceId, batchId, ordinal),
    rep_id: repId,
    rep_name: row.rep_name || null,
    invoice_id: invoiceId,
    invoice_ref: invoiceId,
    paid_batch_id: batchId,
    paid_amount: paid,
    paid_rate: rate,
    kind: isVoid ? "void" : "adjust",
    old_total: round2((accounted * 100) / rate),
    new_total: isVoid ? 0 : round2(total),
    old_commission: accounted,
    new_commission: target,
  };
}

// ── per-rep views ────────────────────────────────────────────────────────────
// Corrections for one rep. When paidRows is given, an OPEN correction counts
// only while its anchor (a paid row with the same invoice_id + batch_id) still
// exists; settled and waived ones are history and always stay.
export function correctionsForRep(corrections, { repId, paidRows } = {}) {
  const list = (corrections || []).filter((c) => c && (repId === undefined || str(c.rep_id) === str(repId)));
  if (!Array.isArray(paidRows)) return list;
  const anchors = new Set(paidRows.filter(Boolean).map((p) => str(p.invoice_id) + "|" + str(p.batch_id)));
  return list.filter((c) => c.status !== "open" || anchors.has(str(c.invoice_id) + "|" + str(c.paid_batch_id)));
}

export const openCorrections = (list) => (list || []).filter((c) => c && c.status === "open");
export const openCorrectionsTotal = (list) => round2(sum(openCorrections(list), (c) => c.delta));
export const settledCorrectionsTotal = (list) => round2(sum((list || []).filter((c) => c && c.status === "settled"), (c) => c.delta));

// Payout netting. payout = invoice commissions being paid + selected open
// corrections. A negative net cannot be paid: the corrections stay OPEN (a
// balance carried forward) and only the invoices are paid.
export function netPayout({ invoiceCommission, selectedCorrections } = {}) {
  const inv = Number.isFinite(Number(invoiceCommission)) ? Number(invoiceCommission) : 0;
  const selected = openCorrections(selectedCorrections);
  const corr = sum(selected, (c) => c.delta);
  const net = inv + corr;
  if (selected.length > 0 && net < -TOLERANCE) {
    return {
      settle: false, negative: true, net: round2(net),
      invoiceCommission: round2(inv), correctionsTotal: round2(corr),
      payoutAmount: round2(inv), settleIds: [], carriedForward: selected,
    };
  }
  return {
    settle: selected.length > 0, negative: false, net: round2(net),
    invoiceCommission: round2(inv), correctionsTotal: round2(corr),
    payoutAmount: round2(net), settleIds: selected.map((c) => c.id), carriedForward: [],
  };
}

// Display totals: pending gains the signed open corrections, paid gains the
// settled ones. Never mixed into the customer-paid based computeRepMetrics.
export function applyCorrectionsToPending(pendingCommission, corrections) {
  const open = openCorrectionsTotal(corrections);
  return { pending: round2((Number(pendingCommission) || 0) + open), correctionsOpen: open };
}
export function applyCorrectionsToTotals({ totalPending, totalPaid, corrections } = {}) {
  const open = openCorrectionsTotal(corrections);
  const settled = settledCorrectionsTotal(corrections);
  return {
    totalPending: round2((Number(totalPending) || 0) + open),
    totalPaid: round2((Number(totalPaid) || 0) + settled),
    openTotal: open,
    settledTotal: settled,
  };
}

export const SETTLED_ANCHORED_MSG = "A correction for this invoice has already been settled with the rep, so this payout cannot be undone from the portal. An opposite correction path is planned; contact a developer if the payout was wrong.";
export const SETTLED_IN_BATCH_MSG = "This payout settled a correction for the rep and cannot be undone from the portal. An opposite correction path is planned; contact a developer if the payout was wrong.";
// A settled claim younger than this is treated as a payout in flight (Settling),
// never as an orphan, and the database refuses to re-open a non-corr claim
// before this age (migration 0031).
export const ORPHAN_MIN_AGE_MS = 10 * 60 * 1000;

// Undo of a payout is blocked while a SETTLED correction is anchored to it
// (re-opening the invoice at full commission would double pay the delta).
export function canUndoInvoicePayout(corrections, paidRow) {
  if (!paidRow) return { ok: true, blockingIds: [] };
  const blocking = (corrections || []).filter((c) => c && c.status === "settled"
    && str(c.invoice_id) === str(paidRow.invoice_id) && str(c.paid_batch_id) === str(paidRow.batch_id));
  if (blocking.length === 0) return { ok: true, blockingIds: [] };
  return {
    ok: false,
    blockingIds: blocking.map((c) => c.id),
    reason: SETTLED_ANCHORED_MSG,
  };
}

// Which paid (invoice, batch) pairs a set of corrections is anchored to.
// scope = { invoiceIds, batchIds } supplied by the caller (derived from the paid
// rows the UI holds). NO rep id is used anywhere: paid rows are matched to a rep
// by id OR name and a correction copies the PAID row's rep_id, so a rep filter
// could miss one. A correction is anchored when its INVOICE is being undone.
// Undo deletes every payout row of the invoice (unmarkInvoiceCommissionPaid
// deletes by invoice_id), and the batch ids the UI holds can be stale or capped,
// so the batch id is deliberately NOT used to narrow anchoring: the invoice alone
// decides (the conservative side). The only cost is that an inert correction
// (its payout was undone earlier) still blocks a later Undo of a re-paid
// invoice until the admin waives it.
export function undoScope(scope) {
  const invoiceIds = new Set(((scope && scope.invoiceIds) || []).map((x) => str(x)).filter(Boolean));
  const batchIds = new Set(((scope && scope.batchIds) || []).map((x) => str(x)));
  const realBatches = new Set(Array.from(batchIds).filter(Boolean));
  const anchored = (c) => invoiceIds.has(str(c.invoice_id));
  return { invoiceIds, batchIds, realBatches, anchored };
}

// Full Undo rule for the payout about to be deleted, decided from the
// (invoice, batch) scope alone (no payments read). Undo is refused when
//   1. a SETTLED correction is anchored to (invoice_id, batch_id): re-opening the
//      invoice at full commission would double pay the delta;
//   2. a correction was SETTLED IN one of the batches: the payout netted it, and
//      the database never lets a claim be re-opened once payment rows carry its
//      batch, so deleting the rows would orphan the settlement;
//   3. an OPEN correction is anchored to it: Undo would make the correction inert
//      and silently drop a clawback (or a top-up) the rep is owed or owes.
// Waived corrections never block. Returns { ok:true } | { ok:false, code, message, blockingIds }.
export function undoBlockers(corrections, scope) {
  const sc = undoScope(scope);
  const list = (corrections || []).filter(Boolean);
  const settledHere = list.filter((c) => c.status === "settled" && c.settled_batch_id && sc.realBatches.has(str(c.settled_batch_id)));
  if (settledHere.length > 0) {
    return { ok: false, code: "settled_in_batch", blockingIds: settledHere.map((c) => c.id), message: SETTLED_IN_BATCH_MSG };
  }
  const settledAnchored = list.filter((c) => c.status === "settled" && sc.anchored(c));
  if (settledAnchored.length > 0) {
    return { ok: false, code: "settled_anchored", blockingIds: settledAnchored.map((c) => c.id), message: SETTLED_ANCHORED_MSG };
  }
  const openAnchored = list.filter((c) => c.status === "open" && sc.anchored(c));
  if (openAnchored.length > 0) {
    return {
      ok: false, code: "open_anchored", blockingIds: openAnchored.map((c) => c.id),
      message: "This payout has an open correction for the rep, so it cannot be undone. Settle the correction or waive it first.",
    };
  }
  return { ok: true, blockingIds: [] };
}

// Settled corrections whose settlement batch produced no payment rows and is
// not a correction-only settlement ('corr-...'): a claim that never completed.
// Only a claim OLDER than ORPHAN_MIN_AGE_MS (server settled_at) is an orphan:
// a younger one is a payout still in flight (see findSettlingClaims) and must
// never be offered for Re-open (it could still write its payment rows).
const claimAgeMs = (c, now) => {
  const t = new Date(c && c.settled_at).getTime();
  return Number.isFinite(t) ? (now === undefined ? Date.now() : now) - t : NaN;
};
const unpaidClaims = ({ corrections, paidRows } = {}) => {
  const batches = new Set((paidRows || []).filter(Boolean).map((p) => str(p.batch_id)));
  return (corrections || []).filter((c) => c && c.status === "settled"
    && c.settled_batch_id && !str(c.settled_batch_id).startsWith("corr-") && !batches.has(str(c.settled_batch_id)));
};
export function findOrphanSettled({ corrections, paidRows, now } = {}) {
  return unpaidClaims({ corrections, paidRows }).filter((c) => claimAgeMs(c, now) > ORPHAN_MIN_AGE_MS);
}
// Younger claims with no payment rows yet: a payout in flight ("Settling...").
// An unparseable / missing settled_at is neither settling nor orphan (the
// database would refuse the re-open as well).
export function findSettlingClaims({ corrections, paidRows, now } = {}) {
  return unpaidClaims({ corrections, paidRows }).filter((c) => { const a = claimAgeMs(c, now); return Number.isFinite(a) && a <= ORPHAN_MIN_AGE_MS; });
}

const norm = (s) => str(s).trim().toLowerCase();

// ── reconcile scan ───────────────────────────────────────────────────────────
// Compares every paid invoice with the invoice as it is now and PROPOSES the
// corrections that are missing (paths that changed an invoice without the
// Commission tab: admin Void, approved rep adjustment / void, stale payout).
// It never inserts. Flagged invoices are skipped for manual review.
// invoices: [{ id, total, status, rep?, repId? }]; legacyPaidIds: optional Set.
export function findNeededCorrections({ paidRows, invoices, corrections, legacyPaidIds } = {}) {
  const proposals = [];
  const flagged = [];
  const byInvoice = new Map();
  (paidRows || []).filter(Boolean).forEach((r) => {
    const k = str(r.invoice_id);
    if (!k) return;
    if (!byInvoice.has(k)) byInvoice.set(k, []);
    byInvoice.get(k).push(r);
  });
  const invById = new Map();
  (invoices || []).filter(Boolean).forEach((inv) => { invById.set(str(inv.id), inv); });

  const legacy = legacyPaidIds instanceof Set ? legacyPaidIds : new Set(legacyPaidIds || []);
  legacy.forEach((id) => {
    if (!byInvoice.has(str(id))) {
      flagged.push({ invoiceId: str(id), code: "legacy_only", reason: "Legacy payout not migrated yet: open the Commission tab on the browser that holds it so the backfill runs." });
    }
  });

  byInvoice.forEach((rows, invoiceId) => {
    const inv = invById.get(invoiceId);
    if (!inv) return; // unknown invoice: nothing to compare against
    const isVoid = norm(inv.status) === "cancelled";
    // A data-health flag is only worth showing when the invoice actually moved
    // away from what was paid (otherwise every unchanged legacy row with an empty
    // rep id, or a short rep name, would be listed forever). Unusable paid data
    // counts as "might have changed".
    const changedFromPaid = (() => {
      const first = rows[0];
      const paid0 = paidRowCommission(first);
      const rate0 = paidRowRate(first);
      const total0 = Number(inv.total);
      if (!Number.isFinite(paid0) || rate0 === null) return true;
      if (!isVoid && !Number.isFinite(total0)) return true;
      const target0 = isVoid ? 0 : (total0 * rate0) / 100;
      return Math.abs(target0 - paid0) >= TOLERANCE;
    })();
    // A prior correction means the invoice may have moved back (a positive delta).
    const hasPriorCorrection = (corrections || []).some((c) => c && str(c.invoice_id) === invoiceId);
    if (!changedFromPaid && !hasPriorCorrection) return;
    if (rows.length > 1) {
      flagged.push({ invoiceId, code: "duplicate_paid_rows", reason: "Duplicate payout rows exist for this invoice: review manually." });
      return;
    }
    const row = rows[0];
    if (!str(row.rep_id).trim()) {
      flagged.push({ invoiceId, code: "empty_rep_id", reason: "The payout row has no rep id: review manually." });
      return;
    }
    const idMismatch = inv.repId != null && str(inv.repId) !== "" && str(inv.repId) !== str(row.rep_id);
    const nameMismatch = !!norm(inv.rep) && !!norm(row.rep_name) && norm(inv.rep) !== norm(row.rep_name);
    if (idMismatch || nameMismatch) {
      flagged.push({ invoiceId, code: "rep_mismatch", reason: "The invoice is now assigned to a different rep than the one who was paid: review manually." });
      return;
    }
    const res = computeCorrection({
      paidRows: rows,
      corrections: (corrections || []).filter((c) => c && str(c.invoice_id) === invoiceId),
      newTotal: inv.total,
      isVoid,
    });
    if (res.ok && res.none) return;
    if (!res.ok) { flagged.push({ invoiceId, code: res.code, reason: res.reason }); return; }
    proposals.push({ ...res, invoiceId, isVoid, newTotal: isVoid ? 0 : Number(inv.total), reason: "invoice changed after payout", source: "reconcile" });
  });
  return { proposals, flagged };
}
